import React, { useEffect, useState } from "react";
import { Pagination, CircularProgress } from "../../components/ui";
import { toast } from "react-toastify";
import axiosInstance from "../../axios/axiosInstance";
import dayjs from "dayjs";

const Audit = () => {
  const [data, setData] = useState<any>([]);
  const [page, setPage] = useState<any>(1);
  const [loading, setLoading] = useState(false);

  const handleGetData = async () => {
    setLoading(true);
    try {
      const resp: any = await axiosInstance.get(
        `/api/request/audit/test?page=${page}&pageSize=15&search`
      );
      if (resp?.status === 200) {
        setData(resp?.data?.data);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.body);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetData();
  }, [page]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Audit Management
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Track and review system audit logs
              </p>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <CircularProgress />
              <p className="text-gray-600 dark:text-gray-300">Loading audit logs...</p>
            </div>
          </div>
        ) : data?.data?.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <i className="fa-solid fa-clipboard-list text-3xl text-gray-400 dark:text-gray-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                No audit logs found
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                There are no audit logs available at the moment
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      SI No.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Requester
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {data?.data?.map((item: any, index: any) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                        {item?.change ? (
                          <span className="font-medium">{item.change}</span>
                        ) : (
                          <>
                            {item?.request || "Unknown action"} -{" "}
                            {(() => {
                              try {
                                const data = JSON.parse(item?.data || "{}");
                                return data?.user_name ||
                                  data?.appartment_name ||
                                  data?.name ||
                                  data?.reg_no ||
                                  data?.visitor_name ||
                                  data?.block_name ||
                                  data?.zone_name ||
                                  data?.group_name ||
                                  data?.team_name ||
                                  data?.role_name ||
                                  "N/A";
                              } catch {
                                return "N/A";
                              }
                            })()}
                          </>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {dayjs(item?.time).format("DD-MM-YYYY HH:mm:ss")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {item?.audit_requester?.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            item?.is_admin_made_change && item?.audit_requester?.role === 0
                              ? "text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-300"
                              : "text-gray-600 bg-gray-50 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {item?.is_admin_made_change && item?.audit_requester?.role === 0
                            ? "Super Admin Changed Data"
                            : "No Change Made"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {data?.totalPages > 1 && (
              <div className="flex justify-center px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <Pagination
                  count={data?.totalPages}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Audit;
